import debounce from 'lodash/debounce';
import { useEffect } from 'react';
import { useLocation } from 'react-router';

/**
 * Custom hook to save and restore scroll position when navigating between pages.
 * Handles the scroll position when navigating using the browser's back/forward buttons.
 * It is close copy of the solution provided in the React Router
 * https://reactrouter.com/web/guides/scroll-restoration
 * It can be remove once we have fully migrated all the code to react-router v7 which uses root.tsx and data loader routes
 * https://api.reactrouter.com/v7/functions/react_router.ScrollRestoration.html
 * https://reactrouter.com/explanation/special-files#roottsx
 */

const useScrollRestoration = (): void => {
  const location = useLocation(); // Get the current location object from react-router

  useEffect(() => {
    // Debounce function to save scroll position to sessionStorage
    const saveScrollPosition = debounce(() => {
      sessionStorage.setItem(location.key, window.scrollY.toString());
    }, 100);

    // Handler to save scroll position before the window unloads
    const handleBeforeUnload = (): void => {
      saveScrollPosition();
    };

    // Handler to restore scroll position when navigating using the browser's back/forward buttons
    const handlePopState = (): void => {
      const popStateSavedPosition = sessionStorage.getItem(location.key);
      if (popStateSavedPosition) {
        window.scrollTo(0, parseFloat(popStateSavedPosition));
      }
    };

    // Scroll to top on new navigations
    const handleNavigation = (): void => {
      const [navEntry] = performance.getEntriesByType(
        'navigation',
      ) as PerformanceNavigationTiming[];

      if (navEntry && navEntry.type !== 'back_forward') {
        window.scrollTo(0, 0);
      }
    };

    // Handler to scroll to top when clicking on an anchor link
    const handleHashChange = (): void => {
      window.scrollTo(0, 0);
    };

    // Add event listeners for beforeunload, popstate, and hashchange events
    window.addEventListener('beforeunload', handleBeforeUnload);
    window.addEventListener('popstate', handlePopState);
    window.addEventListener('hashchange', handleHashChange);

    // Scroll to top for new navigations
    handleNavigation();

    // Cleanup function to remove event listeners and save scroll position
    return () => {
      window.removeEventListener('beforeunload', handleBeforeUnload);
      window.removeEventListener('popstate', handlePopState);
      window.removeEventListener('hashchange', handleHashChange);
      saveScrollPosition();
    };
  }, [location]); // Dependency array to re-run the effect when location changes
};

export default useScrollRestoration;
